import React, { useState } from 'react';
import { parseISO, format } from 'date-fns';
import { nb } from 'date-fns/locale';
import {
  Zap,
  TrendingDown,
  TrendingUp,
  Gauge,
  ChevronDown,
  ChevronUp,
  Percent,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

import { ElectricityPrice, PriceArea } from '../types';
import { calculateElectricitySubsidy, getSubsidyThreshold, getSubsidyThresholdExVat, getNorgesprisPrice } from '../utils/subsidyCalculator';

interface DayOverviewProps {
  prices: ElectricityPrice[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  area?: PriceArea;
}

const EV_CHARGER_KWH = 45;
const LAUNDRY_KWH = 0.8;
const OVEN_KWH = 1.1;
const DUSJ_KWH = 6;

const formatOre = (nok: number) =>
  (nok * 100).toLocaleString('nb-NO', { maximumFractionDigits: 2 });

const DayOverview: React.FC<DayOverviewProps> = ({ prices, selectedDate, onDateChange, area }) => {
  if (prices.length === 0) return null;

  const [showDetails, setShowDetails] = useState(false);
  const [showSubsidyDetails, setShowSubsidyDetails] = useState(false);

  // Get tomorrow's date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = format(selectedDate, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd');
  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  // Current hour price
  const now = new Date();
  const currentHourIndex = prices.findIndex(
    (p) => parseISO(p.time_start) <= now && parseISO(p.time_end) > now
  );
  const currentPrice = prices[currentHourIndex] || null;

  // Basic stats
  const avgPrice =
    prices.reduce((sum, p) => sum + p.NOK_per_kWh, 0) / prices.length;
  const lowestPrice = Math.min(...prices.map((p) => p.NOK_per_kWh));
  const highestPrice = Math.max(...prices.map((p) => p.NOK_per_kWh));

  const lowestHour = prices.find((p) => p.NOK_per_kWh === lowestPrice);
  const highestHour = prices.find((p) => p.NOK_per_kWh === highestPrice);

  // Subsidy info for current hour
  const subsidyInfo = currentPrice
    ? calculateElectricitySubsidy(currentPrice.NOK_per_kWh, area)
    : null;

  // Usage-based cost calculations
  const carChargingCost = currentPrice
    ? (subsidyInfo?.isSubsidyActive
      ? subsidyInfo.priceAfterSubsidy
      : currentPrice.NOK_per_kWh) * EV_CHARGER_KWH
    : null;

  const laundryCost = currentPrice
    ? (subsidyInfo?.isSubsidyActive
      ? subsidyInfo.priceAfterSubsidy
      : currentPrice.NOK_per_kWh) * LAUNDRY_KWH
    : null;

  const ovenCost = currentPrice
    ? (subsidyInfo?.isSubsidyActive
      ? subsidyInfo.priceAfterSubsidy
      : currentPrice.NOK_per_kWh) * OVEN_KWH
    : null;
  const dusjCost = currentPrice
    ? (subsidyInfo?.isSubsidyActive
      ? subsidyInfo.priceAfterSubsidy
      : currentPrice.NOK_per_kWh) * DUSJ_KWH
    : null;


  // Calculate subsidy percentage if active
  const subsidyPercentage = subsidyInfo?.isSubsidyActive 
    ? (subsidyInfo.subsidyAmount / currentPrice?.NOK_per_kWh! * 100) 
    : 0;

  return (
    <div className="rounded-lg shadow-md overflow-hidden mb-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-card">

      {/* Status Banner */}
      <div 
        className="min-h-[3rem] py-2"
        style={{
          containIntrinsicSize: '0 48px',
          contentVisibility: 'auto'
        }}
      >
        {currentPrice && (() => {
        const priceRange = highestPrice - lowestPrice;
        const pricePosition =
          priceRange > 0
            ? (currentPrice.NOK_per_kWh - lowestPrice) / priceRange
            : 0.5;
        const now = new Date();
        const currentIndex = prices.findIndex(
          (p) => parseISO(p.time_start) <= now && parseISO(p.time_end) > now
        );
        const futurePrices = prices.slice(currentIndex + 1);
        const nextCheaperHour = futurePrices.find(
          (p) => p.NOK_per_kWh < currentPrice.NOK_per_kWh * 0.9
        );
        const waitTimeHours = nextCheaperHour
          ? (parseISO(nextCheaperHour.time_start).getTime() - now.getTime()) / (1000 * 60 * 60)
          : null;
        let bannerStyle = '';
        let bannerMessage = '';
        if (pricePosition < 0.2) {
          bannerStyle = 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
          bannerMessage = 'Blant dagens laveste priser – bruk strøm nå!';
        } else if (nextCheaperHour && waitTimeHours !== null && waitTimeHours < 6) {
          bannerStyle = 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
          bannerMessage = `Prisene blir merkbart lavere om ca. ${Math.round(waitTimeHours)} time(r) – vurder å vente?`;
        } else if (pricePosition > 0.8) {
          bannerStyle = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
          bannerMessage = 'Dyrere akkurat nå – og det blir ikke særlig billigere i dag';
        } else {
          bannerStyle = 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
          bannerMessage = 'Rundt gjennomsnittlig pris for dagen – bruk med fornuft';
        }
        return (
          <div 
            className={`px-3 py-2 text-sm font-medium text-center flex items-center justify-center border-b border-t first:border-t-0 ${bannerStyle}`}
            style={{ 
              contentVisibility: 'auto',
              containIntrinsicSize: '0 48px'
            }}
          >
            <span className="inline-block text-center px-2">{bannerMessage}</span>
          </div>
        );
      })() || null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
        {/* Left column: Current Price */}
        <div 
          className="flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm"
          style={{ 
            contentVisibility: 'auto',
            containIntrinsicSize: '0 150px'
          }}
        >
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
            Nåværende pris ({format(now, 'HH:mm', { locale: nb })})
          </div>
          <div className="flex items-center space-x-3" aria-live="polite" role="status">
            <Zap className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <div className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white">
              {subsidyInfo?.isSubsidyActive ? (
                <>
                  <span className="text-gray-600 dark:text-gray-300 line-through text-2xl mr-2">
                    {currentPrice?.NOK_per_kWh.toFixed(2)}
                  </span>
                  <span className="text-green-700 dark:text-green-200">
                    {subsidyInfo.priceAfterSubsidy.toFixed(2)}
                  </span>
                </>
              ) : (
                <span>{currentPrice?.NOK_per_kWh.toFixed(2)}</span>
              )}
              <span className="text-2xl ml-1">kr</span>
            </div>
          </div>
          
          {/* Subsidy Status Badge */}
          {currentPrice && (
            <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full ${
              subsidyInfo?.isSubsidyActive 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {subsidyInfo?.isSubsidyActive ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  <span className="text-xs font-medium">Strømstøtte aktiv ({subsidyPercentage.toFixed(0)}%)</span>
                  {subsidyInfo?.isSubsidyActive && (
                    <span className="ml-1 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded uppercase text-[10px] font-bold animate-pulse">
                      NÅ
                    </span>
                  )}
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 mr-1" />
                  <span className="text-xs font-medium">Ingen strømstøtte</span>
                </>
              )}
            </div>
          )}

          {/* Subsidy Info Button */}
          <button 
            onClick={() => setShowSubsidyDetails(!showSubsidyDetails)}
            className="mt-2 text-xs text-blue-700 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
            aria-expanded={showSubsidyDetails}
          >
            <Info className="h-3 w-3 mr-1" />
            <span>{showSubsidyDetails ? 'Skjul' : 'Vis'} strømstøttedetaljer</span>
          </button>
        </div>

        {/* Middle Column: Min-max prices */}
        <div className="grid grid-cols-2 gap-2">
          {/* Cheapest Hour */}
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 text-center">
            <TrendingDown className="h-5 w-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
            <div className="text-sm text-gray-700 dark:text-gray-200">Billigste time</div>
            <div className="text-lg font-bold text-green-800 dark:text-green-300">
              {lowestPrice.toFixed(2)} kr
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Kl. {lowestHour
                ? format(parseISO(lowestHour.time_start), 'HH:mm', { locale: nb })
                : '–'}
            </div>
          </div>
          
          {/* Most Expensive Hour */}
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-center">
            <TrendingUp className="h-5 w-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
            <div className="text-sm text-gray-700 dark:text-gray-200">Dyreste time</div>
            <div className="text-lg font-bold text-red-800 dark:text-red-300">
              {highestPrice.toFixed(2)} kr
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Kl. {highestHour
                ? format(parseISO(highestHour.time_start), 'HH:mm', { locale: nb })
                : '–'}
            </div>
          </div>
          
          {/* Average Price */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800 text-center col-span-2">
            <Gauge className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mx-auto mb-1" />
            <div className="text-sm text-gray-700 dark:text-gray-200">Gjennomsnittspris</div>
            <div className="text-lg font-bold text-yellow-800 dark:text-yellow-300">
              {avgPrice.toFixed(2)} kr
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">For hele dagen</div>
          </div>
        </div>

        {/* Right Column: Cost Examples */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Kostnader nå:</span>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>🚗 Nissan Leaf (10-80%)</span>
              <span className="font-medium">
                {carChargingCost !== null ? `${carChargingCost.toFixed(0)} kr` : '–'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>🚿 Dusj (10 min) </span>
              <span className="font-medium">
                {dusjCost !== null ? `${dusjCost.toFixed(1)} kr` : '–'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>🧺 Vaskemaskin (1 vask)</span>
              <span className="font-medium">
                {laundryCost !== null ? `${laundryCost.toFixed(1)} kr` : '–'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>🍕 Grandis (30 min)</span>
              <span className="font-medium">
                {ovenCost !== null ? `${ovenCost.toFixed(1)} kr` : '–'}
              </span>
            </div>

          </div>
          
          {showDetails && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500 mb-1">
                {subsidyInfo?.isSubsidyActive 
                  ? "Prisene inkluderer strømstøtte"
                  : "Ingen strømstøtte tilgjengelig"
                }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Subsidy Information */}
      {showSubsidyDetails && subsidyInfo && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center">
                <Percent className="h-4 w-4 mr-1 text-blue-600" />
                Om strømstøtten
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                I {new Date().getFullYear()} dekker staten 90 % av strømprisen som overstiger {formatOre(getSubsidyThresholdExVat())} øre/kWh (uten mva.)
                {area === 'NO4'
                  ? '. Nord-Norge er fritatt for mva., så grensen er den samme for deg.'
                  : ` eller ${formatOre(getSubsidyThreshold(area))} øre/kWh (med mva.).`}
                {' '}Har du Norgespris, får du ikke strømstøtte, men betaler fast {formatOre(getNorgesprisPrice(area))} øre/kWh.
              </p>
              <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 text-sm">
                <div className="font-medium mb-1 text-gray-700 dark:text-gray-200">Beregning for dagens pris:</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {subsidyInfo.isSubsidyActive ? (
                    <>
                      <div className="text-gray-600 dark:text-gray-300">Pris med mva:</div>
                      <div className="text-right text-gray-700 dark:text-gray-200">{currentPrice?.NOK_per_kWh.toFixed(2)} kr/kWh</div>
                      <div className="text-gray-600 dark:text-gray-300">Støttebeløp:</div>
                      <div className="text-right text-green-600">-{subsidyInfo.subsidyAmount.toFixed(2)} kr/kWh</div>
                      <div className="font-medium text-gray-700 dark:text-gray-200">Din pris:</div>
                      <div className="text-right font-medium text-green-700">{subsidyInfo.priceAfterSubsidy.toFixed(2)} kr/kWh</div>
                    </>
                  ) : (
                    <div className="col-span-2 text-gray-600 dark:text-gray-300">
                      Ingen strømstøtte da prisen ({currentPrice?.NOK_per_kWh.toFixed(2)} kr/kWh) er under grensen for støtte ({formatOre(getSubsidyThreshold(area))} øre/kWh{area === 'NO4' ? '' : ' med mva.'})
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center">
                <Zap className="h-4 w-4 mr-1 text-blue-600" />
                Strømstøtte ved ulike priser
              </h4>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[1.00, 1.50, 2.00, 3.00, 5.00, 10.00].map((price, idx) => {
                  const calc = calculateElectricitySubsidy(price, area);
                  return (
                    <div key={idx} className={`${
                      calc.isSubsidyActive 
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800' 
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    } p-2 rounded border`}>
                      <div className="font-medium text-gray-700 dark:text-gray-200">{price.toFixed(2)} kr</div>
                      <div className={calc.isSubsidyActive ? "text-green-600" : "text-gray-400"}>
                        {calc.isSubsidyActive ? `-${calc.subsidyAmount.toFixed(2)} kr` : "Ingen støtte"}
                      </div>
                      <div className="mt-1 border-t border-gray-200 dark:border-gray-700 pt-1 font-medium text-gray-700 dark:text-gray-200">
                        = {calc.priceAfterSubsidy.toFixed(2)} kr
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayOverview;